import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Plus,
  Trash2,
  Mail,
  FileText,
  Loader,
  AlertCircle,
  RefreshCw,
  Send,
  CheckCircle,
  X,
} from 'lucide-react';
import EmailRecipientModal from './EmailRecipientModal';
import ManagementDeleteModal from './ManagementDeleteModal';
import GenerateReportModal from './GenerateReportModal';
import { getEmailRecipients, saveEmailRecipients, triggerReport } from './../services/managementService';

interface TabDisplayConfig {
  title: string;
  description: string;
  emptyDescription: string;
  reportConfirmText: string;
}

const TAB_DISPLAY_CONFIG: Record<string, TabDisplayConfig> = {
  emails_pedidos_sem_faturamento_magento: {
    title: 'Pedidos sem Faturamento',
    description: 'Gerencie os e-mails que recebem diariamente informações sobre pedidos sem faturamento',
    emptyDescription: 'Adicione e-mails para receber informações diárias sobre pedidos sem faturamento',
    reportConfirmText: 'Os relatórios de pedidos sem faturamento serão criados e enviados dentro de alguns minutos',
  },
  'relatorio-gaps-precificacao': {
    title: 'Relatório - Gaps Precificação',
    description: 'Gerencie os e-mails que recebem o relatório de gaps de precificação',
    emptyDescription: 'Adicione e-mails para receber o relatório de gaps de precificação',
    reportConfirmText: 'Os relatórios de gaps de precificação serão criados e enviados dentro de alguns minutos',
  },
  'relatorio-maiores-descontos': {
    title: 'Relatório - Maiores Descontos',
    description: 'Gerencie os e-mails que recebem o relatório de maiores descontos',
    emptyDescription: 'Adicione e-mails para receber o relatório de maiores descontos',
    reportConfirmText: 'Os relatórios de maiores descontos serão criados e enviados dentro de alguns minutos',
  },
};

const DEFAULT_DISPLAY_CONFIG: TabDisplayConfig = {
  title: 'Destinatários de E-mail',
  description: 'Gerencie os e-mails destinatários',
  emptyDescription: 'Adicione e-mails para receber relatórios',
  reportConfirmText: 'Os relatórios serão criados e enviados dentro de alguns minutos',
};

interface EmailRecipientsTabProps {
  permissionName: any;
}

export default function EmailRecipientsTab({ permissionName }: EmailRecipientsTabProps) {
  const [emails, setEmails] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<string | undefined>();
  // Tracks which single email (if any) the report modal was opened for
  const [reportTargetEmail, setReportTargetEmail] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const config = TAB_DISPLAY_CONFIG[permissionName] ?? DEFAULT_DISPLAY_CONFIG;

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getEmailRecipients(permissionName);
      setEmails(data);
    } catch (err) {
      setError('Erro ao carregar destinatários. Tente novamente.');
      console.error('Error fetching email recipients:', err);
    } finally {
      setIsLoading(false);
    }
  }, [permissionName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredEmails = emails.filter((email) =>
    email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setIsModalOpen(true);
  };

  const handleDelete = (email: string) => {
    setEmailToDelete(email);
    setIsDeleteModalOpen(true);
  };

  /** Opens the report modal for ALL recipients */
  const handleOpenReportAll = () => {
    setReportTargetEmail(undefined);
    setIsReportModalOpen(true);
  };

  /** Opens the report modal for a SINGLE recipient */
  const handleOpenReportSingle = (email: string) => {
    setReportTargetEmail(email);
    setIsReportModalOpen(true);
  };

  const handleSave = async (data: { email: string }) => {
    setIsSubmitting(true);
    try {
      const newEmails: any = [...emails, data.email];
      await saveEmailRecipients(permissionName, newEmails);
      setEmails(newEmails);
      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving email recipient:', err);
      setError('Erro ao salvar destinatário. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!emailToDelete) return;

    setIsSubmitting(true);
    try {
      const newEmails = emails.filter((e) => e !== emailToDelete);
      await saveEmailRecipients(permissionName, newEmails);
      setEmails(newEmails);
      setIsDeleteModalOpen(false);
      setEmailToDelete(undefined);
    } catch (err) {
      console.error('Error deleting email recipient:', err);
      setError('Erro ao excluir destinatário. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateReport = async () => {
    setIsGeneratingReport(true);
    setError(null);
    try {
      // Pass the target email when sending to a single recipient
      triggerReport(permissionName, reportTargetEmail);
      setIsReportModalOpen(false);
      setReportTargetEmail(undefined);
      const target = reportTargetEmail
        ? `para ${reportTargetEmail}`
        : 'para todos os destinatários';
      setSuccessMessage(
        `Relatório solicitado com sucesso! O e-mail será enviado ${target} em alguns minutos.`
      );
      setTimeout(() => setSuccessMessage(null), 8000);
    } catch (err) {
      console.error('Error generating report:', err);
      setError('Erro ao gerar relatórios. Tente novamente.');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-600 mb-4" />
        <p className="text-gray-600">Carregando destinatários...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {config.title}
            </h2>
            <p className="text-sm text-gray-500">
              {config.description}
            </p>
          </div>
        </div>
      </div>

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-green-600 hover:text-green-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={fetchData}
            className="text-red-600 hover:text-red-800 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por e-mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Atualizar"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleOpenReportAll}
              disabled={emails.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              <span className="hidden sm:inline">Gerar Relatórios</span>
              <span className="sm:hidden">Gerar</span>
            </button>
            <button
              onClick={handleAdd}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              <span>Novo E-mail</span>
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {filteredEmails.length === 0 ? (
          <div className="p-12 text-center">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Nenhum e-mail encontrado' : 'Nenhum e-mail cadastrado'}
            </h3>
            <p className="text-gray-500 mb-4">
              {searchTerm
                ? 'Tente buscar com outros termos'
                : config.emptyDescription}
            </p>
            {!searchTerm && (
              <button
                onClick={handleAdd}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar E-mail</span>
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      E-mail
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredEmails.map((email, index) => (
                    <tr key={`${email}-${index}`} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Mail className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {email}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleOpenReportSingle(email)}
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Enviar relatório para este e-mail"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(email)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile list */}
            <div className="md:hidden divide-y divide-gray-200">
              {filteredEmails.map((email, index) => (
                <div key={`${email}-${index}`} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="font-medium text-gray-900 truncate">
                        {email}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleOpenReportSingle(email)}
                        className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title="Enviar relatório para este e-mail"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(email)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <p className="text-sm text-gray-500 text-center">
        {filteredEmails.length} {filteredEmails.length === 1 ? 'e-mail' : 'e-mails'} cadastrado{filteredEmails.length !== 1 ? 's' : ''}
      </p>

      <EmailRecipientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        isSubmitting={isSubmitting}
      />

      <ManagementDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setEmailToDelete(undefined);
        }}
        onConfirm={handleConfirmDelete}
        itemName={emailToDelete || ''}
        itemType="destinatário"
        isSubmitting={isSubmitting}
      />

      <GenerateReportModal
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setReportTargetEmail(undefined);
        }}
        onConfirm={handleGenerateReport}
        isSubmitting={isGeneratingReport}
        emailCount={reportTargetEmail ? 1 : emails.length}
        confirmText={config.reportConfirmText}
        targetEmail={reportTargetEmail}
      />
    </div>
  );
}
